/**
 * KeyboardShortcutsModal — generic modal listing keyboard shortcuts
 * for a given page.
 *
 * Extracted from `PortfolioDashboard.tsx` in Sprint V2.B3 (cf.
 * `docs/v2/PORTFOLIO_OFFCHAIN_REFONTE.md`). Was inline JSX with
 * 7 hardcoded shortcuts and 11 inline `style={{}}` instances; now
 * accepts a `shortcuts` array so any page can host its own help
 * modal with the same look & feel.
 *
 * The component is a no-op on touch devices — keyboard help isn't
 * useful when there's no keyboard. Callers are still expected to
 * gate the `isOpen` flag (we duplicate the check defensively).
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';

export interface KeyboardShortcutEntry {
  /** Human-readable description rendered on the left. */
  label: string;
  /** Keys to render on the right as a list of `<kbd>` chips.
   *  - String for a single chip ("A", "?")
   *  - Array for a chord ("Ctrl + E" → ["Ctrl", "E"], rendered with " + ")
   *  - Array of arrays for alternative bindings (["Shift + H", "?"]
   *    → two separate `<kbd>` chips with " / " between them) */
  keys: string | string[] | string[][];
}

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Defaults to `t('common.keyboardShortcuts')` if omitted. */
  title?: string;
  shortcuts: KeyboardShortcutEntry[];
  /** Optional info paragraph at the bottom (e.g. "shortcuts disabled
   *  while typing in inputs"). */
  footerNote?: ReactNode;
}

/** Helper — true when the runtime exposes touch input.
 *  Defensive check so the modal never opens on touch devices even if
 *  a caller forgets to gate `isOpen`. */
function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/** Renders one shortcut row's keys part — handles single key, chord,
 *  and alternative bindings via the `keys` prop type. */
function ShortcutKeys({ keys }: { keys: KeyboardShortcutEntry['keys'] }) {
  if (typeof keys === 'string') {
    return <kbd className="kbd">{keys}</kbd>;
  }
  // Array of arrays → alternatives separated by " / "
  if (Array.isArray(keys[0])) {
    const alternatives = keys as string[][];
    return (
      <span>
        {alternatives.map((chord, idx) => (
          <span key={idx}>
            {idx > 0 && ' / '}
            <kbd className="kbd">{chord.join(' + ')}</kbd>
          </span>
        ))}
      </span>
    );
  }
  // Single chord array → join with " + "
  return <kbd className="kbd">{(keys as string[]).join(' + ')}</kbd>;
}

export function KeyboardShortcutsModal({
  isOpen,
  onClose,
  title,
  shortcuts,
  footerNote,
}: KeyboardShortcutsModalProps) {
  const { t } = useTranslation();

  if (!isOpen || isTouchDevice()) return null;

  const heading = title ?? t('common.keyboardShortcuts', 'Raccourcis clavier');

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="keyboard-shortcuts-modal-title"
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '500px', width: '100%' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <h2
            id="keyboard-shortcuts-modal-title"
            style={{ margin: 0, fontSize: '18px' }}
          >
            <Icons.Keyboard
              size={20}
              style={{ verticalAlign: 'middle', marginRight: '8px' }}
            />
            {heading}
          </h2>
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            aria-label={t('common.close', 'Fermer')}
            style={{ padding: '6px' }}
          >
            <Icons.X size={20} />
          </button>
        </div>

        <ul
          style={{
            display: 'grid',
            gap: '12px',
            fontSize: '14px',
            listStyle: 'none',
            padding: 0,
            margin: 0,
          }}
        >
          {shortcuts.map((entry, idx) => (
            <li
              key={idx}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderRadius: '4px',
                backgroundColor: 'var(--bg-secondary)',
              }}
            >
              <span>{entry.label}</span>
              <ShortcutKeys keys={entry.keys} />
            </li>
          ))}
        </ul>

        {footerNote && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              borderRadius: '4px',
              backgroundColor: 'var(--bg-secondary)',
              fontSize: '12px',
              color: 'var(--muted)',
            }}
          >
            <Icons.Info
              size={14}
              style={{ verticalAlign: 'middle', marginRight: '6px' }}
            />
            {footerNote}
          </div>
        )}
      </div>
    </div>
  );
}
